import React, { FC, ReactNode, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { PageTitle, useIntl, Button, Grid, GridColumn, Card, Table, FormattedMessage, Checkbox, Tooltip } from "@metsooutotec/modes-react-components"
import { useNavigate, useParams } from "react-router-dom"
import usersStore from "features/users/store/users-store"
import styles from "./user-page.module.css"
import { itemViewDetailsContainerStyles, itemViewDetailsTableStyles } from "components/common-style/common-style"
import customerStore from "features/organizations/customers/store/customer-store"
import { groupPermissionsByCustomerAndSite } from "features/users/util/user-permissions-grouping"
import { format } from "date-fns"
import { WSUser } from "features/users/models/users-models"
import WSUserModal from "features/users/components/user-modal/user-modal"
import GenericDeleteModal from "components/delete-modal/generic-gelete-modal"
import ResetPasswordModal from "features/users/components/user-reset-password-modal/user-reset-password-modal"
import UserRolesModal from "features/users/components/user-roles-modal/user-roles-modal"

// --- Custom hook to encapsulate data logic ---
function useUserPageData(userId?: string) {
    const { userMap } = usersStore
    const { customerMap, customers, sites } = customerStore
    const user: WSUser | undefined = userId ? userMap[userId] : undefined

    const organization = useMemo(() => {
        return user?.customerId ? customerMap[user.customerId]?.name ?? "" : ""
    }, [user, customerMap])

    const fields = useMemo(
        () => [
            { id: "user.displayName", value: user?.displayName },
            { id: "user.email", value: user?.email },
            { id: "user.organization", value: organization },
            { id: "user.jobTitle", value: user?.jobTitle },
            { id: "user.description", value: user?.description },
        ],
        [user, organization],
    )

    const groupedPermissions = useMemo(
        () => (user?.permissions ? groupPermissionsByCustomerAndSite(user.permissions, customers, sites) : {}),
        [user, customers],
    )
    return { user, fields, groupedPermissions }
}

// --- Presentational components ---
const UserInfoCard: FC<{ fields: { id: string; value?: string }[]; setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }> = ({
    fields,
    setIsOpen,
}) => (
    <Card
        content={
            <Table style={itemViewDetailsTableStyles} tight>
                <Table.Body>
                    {fields.map(({ id, value }) => (
                        <Table.Row key={id}>
                            <Table.HeaderCell className={styles.userItemViewCellStyles}>
                                <FormattedMessage id={id} />
                            </Table.HeaderCell>
                            <Table.Cell className={styles.userItemViewCellStyles}>
                                {value || <FormattedMessage id='global.empty' defaultMessage='Empty' />}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        }
        data-cy='card'
        header={<FormattedMessage id='user.information' />}
        rightHeaderItem={<Button icon='icon-edit-16' subtle onClick={() => setIsOpen(true)} />}
    />
)

const UserAuthInfoCard: FC<{ user: WSUser; setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }> = ({ user, setIsOpen }) => (
    <Card
        content={
            <div className={styles.userSecondCardContents}>
                <h6>
                    <FormattedMessage id='user.lastLogin' />
                </h6>
                {user.lastAuthEvent && (
                    <>
                        {format(new Date(user.lastAuthEvent.eventDate), "dd/MM/yy HH:mm")} {user.lastAuthEvent.eventType}{" "}
                        {user.lastAuthEvent.remoteIpAddress}
                    </>
                )}
                <br />
                <h6>
                    <FormattedMessage id='user.roles' />
                </h6>
                <Table style={itemViewDetailsTableStyles} tight>
                    <Table.Body>
                        {user.roles?.map(({ id, name, assigned }) => (
                            <Table.Row key={id}>
                                <Checkbox id={name} label={name} checked={assigned} />
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        }
        data-cy='card'
        header={<FormattedMessage id='user.authInformation' />}
        rightHeaderItem={<Button icon='icon-edit-16' onClick={() => setIsOpen(true)} subtle />}
    />
)

const PermissionsCard: FC<{
    groupedPermissions: Record<string, { customerName: string; sites: string[] }>
}> = ({ groupedPermissions }) => (
    <Card
        content={
            <div className={styles.userThirdCardContents}>
                <ul>
                    {Object.entries(groupedPermissions).map(([customerId, { customerName, sites }]) => (
                        <li key={customerId}>
                            <strong>{customerName}</strong>
                            <ul>
                                {sites.map(siteName => (
                                    <li key={siteName}>{siteName}</li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>
        }
        data-cy='card'
        header={<FormattedMessage id='user.permissionsInformation' />}
    />
)

// --- Main page component ---
const UserPage: FC = observer(() => {
    const { userId } = useParams<{ userId: string }>()
    const { loadUserAsync } = usersStore
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { user, fields, groupedPermissions } = useUserPageData(userId)
    const [isEditUserModalOpen, setEditUserModalOpen] = useState<boolean>(false)
    const [isDeleteUserModalOpen, setDeleteUserModalOpen] = useState<boolean>(false)
    const [isResetUserModalOpen, setResetUserModalOpen] = useState<boolean>(false)
    const [isRolesUserModalOpen, setRolesUserModalOpen] = useState<boolean>(false)

    // Early return if user is not found
    if (!user) {
        return (
            <div>
                <FormattedMessage id='user.notFound' defaultMessage='User not found' />
            </div>
        )
    }

    const rightPageButtons: ReactNode = (
        <>
            <Tooltip
                content={formatMessage("user.reload")}
                data-cy='tooltip'
                position='top center'
                trigger={
                    <Button
                        icon='icon-reload-16'
                        primary
                        onClick={() => {
                            loadUserAsync(userId as string)
                        }}
                        subtle
                    />
                }
            />
            <Button key={"user.delete"} icon='icon-trash-16' labelPosition='right' secondary onClick={() => setDeleteUserModalOpen(true)}>
                {formatMessage("user.delete")}
            </Button>
            <Button key={"user.resetPassword"} onClick={() => setResetUserModalOpen(true)} primary>
                {formatMessage("user.resetPassword")}
            </Button>
        </>
    )

    const deleteModal: ReactNode = (
        <GenericDeleteModal<WSUser>
            isOpen={isDeleteUserModalOpen}
            setIsOpen={setDeleteUserModalOpen}
            entity={user}
            getEntityName={entity => entity.displayName}
            onDelete={async entity => await usersStore.deleteEntity(entity.id)}
            navigateOnDelete={{
                path: "/users-management",
                state: { deleted: true, entityName: user.displayName },
            }}
            i18nKeys={{
                header: "userDelete.header",
                content: "userDelete.content",
                cancel: "modal.cancel",
                confirm: "modal.confirm",
            }}
            toastMessage={`${user.displayName} has been deleted!`}
        />
    )

    return (
        <>
            <PageTitle
                breadcrumbsPath={[
                    { text: "Users", to: "/users-management" },
                    { text: `${user.displayName}`, to: `/users-management/${userId}` },
                ]}
                additionalBreadcrumbProps={{ "data-cy": "page-title-breadcrumbs" }}
                breadcrumbIcon='icon-arrow-left-16'
                onPathChange={navigate}
                title={user.displayName}
                extraItems={rightPageButtons}
            />
            <div
                style={{
                    ...itemViewDetailsContainerStyles,
                    maxHeight: "88%",
                }}
            >
                <Grid columns={2}>
                    <GridColumn className={styles.userDetailsFirstColumn}>
                        <UserInfoCard fields={fields} setIsOpen={setEditUserModalOpen} />
                        <UserAuthInfoCard user={user} setIsOpen={setRolesUserModalOpen} />
                    </GridColumn>
                    <GridColumn>
                        <PermissionsCard groupedPermissions={groupedPermissions} />
                    </GridColumn>
                </Grid>
            </div>
            {isEditUserModalOpen && <WSUserModal isOpen={isEditUserModalOpen} setIsOpen={setEditUserModalOpen} user={user} />}
            {isDeleteUserModalOpen && deleteModal}
            {isResetUserModalOpen && <ResetPasswordModal isOpen={isResetUserModalOpen} setIsOpen={setResetUserModalOpen} userId={userId as string} />}
            {isRolesUserModalOpen && <UserRolesModal isOpen={isRolesUserModalOpen} setIsOpen={setRolesUserModalOpen} user={user} />}
        </>
    )
})

export default UserPage
